/**
 * Notification dropdown panel that shows list of notifications
 * Following container/presentation pattern
 *
 * Uses Facebook-style two-tier notification system:
 * - Badge clears when panel opens (marks as seen)
 * - Notifications remain unread (highlighted) until clicked
 */
import { Settings, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import React, { useEffect, useRef } from 'react';

import { useTranslation } from '@splitfiesta/i18n';
import type { NotificationResponseDto } from '@splitfiesta/shared';

import LoadingState from '@/components/shared/LoadingState';

import {
  useMarkNotificationsAsReadMutation,
  useMarkNotificationsAsSeenMutation,
  useNotificationsInfiniteQuery,
} from '@/hooks/queries/useNotifications';

import NotificationList from './NotificationList';

interface NotificationDropdownProps {
  onClose: () => void;
}

/**
 * Container component for notification dropdown
 */
export default function NotificationDropdown({
  onClose,
}: NotificationDropdownProps) {
  const { t, ready } = useTranslation('notifications');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const markAllAsRead = useMarkNotificationsAsReadMutation();
  const markAsSeen = useMarkNotificationsAsSeenMutation();

  // Fetch notifications with infinite scrolling
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useNotificationsInfiniteQuery({ limit: 25 });

  // Mark all notifications as seen when dropdown opens (Facebook-style UX)
  // This clears the badge count immediately
  useEffect(() => {
    markAsSeen.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close dropdown when clicking outside (but not on the notification bell)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking on the notification bell or dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        // Check if the click is on the notification bell button
        const notificationBell = document.querySelector(
          '[data-testid="notification-bell"]'
        );
        if (notificationBell && notificationBell.contains(target)) {
          // Let the bell handle the toggle, don't close here
          return;
        }
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleMarkAllAsRead = async () => {
    await markAllAsRead.mutateAsync({ markAll: true });
  };

  // Extract notifications from infinite query pages
  // Handle both new cursor-based and legacy response formats
  const notifications =
    data?.pages.flatMap(page => {
      // Type-safe access to page data
      const pageData = page.data as { items?: NotificationResponseDto[] };
      return pageData?.items || [];
    }) || [];

  const unreadCount = (() => {
    const firstPage = data?.pages[0]?.data;
    if (firstPage && 'unreadCount' in firstPage) {
      return firstPage.unreadCount;
    }
    return 0;
  })();

  // Don't render until translations are ready
  if (!ready) {
    return null;
  }

  return (
    <NotificationDropdownPresentation
      ref={dropdownRef}
      t={t}
      isLoading={isLoading}
      isError={isError}
      notifications={notifications}
      unreadCount={unreadCount}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onClose={onClose}
      onMarkAllAsRead={handleMarkAllAsRead}
      onLoadMore={fetchNextPage}
      isMarkingAllAsRead={markAllAsRead.isPending}
    />
  );
}

/**
 * Presentation component for notification dropdown UI
 */
interface NotificationDropdownPresentationProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  isLoading: boolean;
  isError: boolean;
  notifications: NotificationResponseDto[];
  unreadCount: number;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  onClose: () => void;
  onMarkAllAsRead: () => void;
  onLoadMore: () => void;
  isMarkingAllAsRead: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

function NotificationDropdownPresentation({
  t,
  isLoading,
  isError,
  notifications,
  unreadCount,
  hasNextPage,
  isFetchingNextPage,
  onClose,
  onMarkAllAsRead,
  onLoadMore,
  isMarkingAllAsRead,
  ref,
}: NotificationDropdownPresentationProps) {
  return (
    <div
      ref={ref}
      className="absolute right-0 top-12 z-50 w-96 rounded-lg bg-white shadow-xl dark:bg-gray-800"
      data-testid="notification-dropdown"
    >
      {/* Header */}
      <div className="border-b border-gray-200 p-4 dark:border-gray-700">
        {/* First row: Title and icons */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('title')}
          </h3>
          <div className="flex items-center gap-1">
            <Link
              to="/settings"
              className="group relative rounded-lg p-2 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              title={t('settings')}
              aria-label={t('settings')}
              data-testid="notification-settings"
            >
              <Settings className="h-5 w-5" />
              {/* Tooltip */}
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-sm bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-gray-700">
                {t('settings')}
              </span>
            </Link>
            <button
              onClick={onClose}
              className="group relative rounded-lg p-2 text-gray-500 transition-all hover:bg-gray-100 hover:text-gray-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              title={t('close')}
              aria-label={t('close')}
              data-testid="close-notifications"
            >
              <X className="h-5 w-5" />
              {/* Tooltip */}
              <span className="pointer-events-none absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-sm bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 dark:bg-gray-700">
                {t('close')}
              </span>
            </button>
          </div>
        </div>
        {/* Second row: Unread count and mark all read (full width) */}
        {unreadCount > 0 && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('unread_count', { count: unreadCount })}
            </span>
            <span className="text-sm text-gray-400 dark:text-gray-500">•</span>
            <button
              onClick={onMarkAllAsRead}
              disabled={isMarkingAllAsRead}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 focus:outline-hidden focus:underline disabled:opacity-50 disabled:cursor-not-allowed dark:text-primary-400 dark:hover:text-primary-300"
              data-testid="mark-all-read"
            >
              {t('mark_all_read')}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-h-96 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center p-8">
            <LoadingState message={t('loading.text')} />
          </div>
        )}

        {isError && (
          <div className="p-8 text-center">
            <p className="text-red-500">{t('error_loading')}</p>
          </div>
        )}

        {!isLoading && !isError && notifications.length === 0 && (
          <div className="p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400">
              {t('no_notifications')}
            </p>
          </div>
        )}

        {!isLoading && !isError && notifications.length > 0 && (
          <>
            <NotificationList
              notifications={notifications}
              showGroupName={true}
            />

            {hasNextPage && (
              <div className="border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={onLoadMore}
                  disabled={isFetchingNextPage}
                  className="w-full p-3 text-center text-sm text-primary-600 hover:bg-gray-50 dark:text-primary-400 dark:hover:bg-gray-700"
                  data-testid="load-more-notifications"
                >
                  {isFetchingNextPage ? t('loading_more') : t('load_more')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 p-3 dark:border-gray-700">
        <Link
          to="/notifications"
          className="block text-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          onClick={onClose}
          data-testid="view-all-notifications"
        >
          {t('view_all')}
        </Link>
      </div>
    </div>
  );
}
